import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAppDispatch, useAppStore } from '../../redux/app/hooks';
import { setUserMemory } from '../../redux/features/app/appSlice';
import { MemoryService } from '../services/MemoryService';
import { UserMemory } from '../models/UserMemory';

// Two-part guard so a stale GET can never clobber a mutation's result — neither a GET that started
// before a mutation nor one that started *during* one. A mutation dispatches its optimistic value
// once and (for deletes) doesn't dispatch again on success, so a GET that resolves last would
// otherwise win. mutationSeq is bumped at the start of every mutation: a GET that captured an older
// seq has been superseded. activeMutations counts in-flight mutations: a GET that begins while any
// mutation is running is barred from committing, since it can carry pre-mutation server state and
// finish after the mutation settles. Either way the mutation's own optimistic update + confirm stays
// authoritative. Both windows are reachable via a silent-OIDC-renewal background refetch overlapping
// a user opt-in / delete.
//
// These live at MODULE scope, not useRef, on purpose: the guard has to be shared across every
// useUserMemory instance. The app-root seed hook (useInitialUserMemory) only ever fetches, so a
// per-instance guard there sits permanently disarmed — its background GET would then commit stale
// server state over an opt-out/delete in flight in the Settings or Minnen instance, resurrecting the
// just-cleared notes. One logged-in principal per tab, so a single shared guard is correct.
let mutationSeq = 0;
let activeMutations = 0;
// A mutation's confirm/revert is a whole-object snapshot, so writing it after a newer mutation has
// started would overwrite that mutation's value (e.g. a failed delete restoring notes a later opt-out
// already purged). Only the latest mutation writes its snapshot; any overlap sets resyncNeeded, and
// the last mutation to settle refetches server state instead. It refetches only once activeMutations
// is back to 0, or the GET guard above would discard that GET too. The flag clears only when a GET
// actually commits: a failed refetch (the 429 that failed the mutations often fails it too) or one
// discarded by a newer mutation leaves it set, so the next settle retries.
let resyncNeeded = false;

// Call immediately before the try whose finally calls settleMutation, so the decrement is always
// paired with this increment. The counters are module-scoped, so a leaked increment would disarm
// every fetch in the tab, not just this instance.
const armMutation = (): number => {
    if (activeMutations > 0) {
        resyncNeeded = true;
    }
    mutationSeq += 1;
    activeMutations += 1;
    return mutationSeq;
};

export const useUserMemory = () => {
    const auth = useAuth();
    const dispatch = useAppDispatch();
    // Mutations read their baseline from the shared store when they run, not from a per-instance
    // snapshot: several instances (Settings, Minnen, the app-root seed hook) share this state and only
    // one of them may have fetched (#2532). Reading at call time also sees an optimistic value that an
    // earlier mutation dispatched in the same tick.
    const store = useAppStore();
    const token = auth.user?.access_token;

    const memoryService = useMemo(() => new MemoryService(token ?? ''), [token]);

    const fetchMemory = useCallback(async (): Promise<UserMemory | undefined> => {
        const seqAtStart = mutationSeq;
        const bornDuringMutation = activeMutations > 0;
        try {
            const memory = await memoryService.getMemoryAsync();
            if (bornDuringMutation || mutationSeq !== seqAtStart) {
                // A mutation was in flight when this GET began, or started while it was in flight — its
                // state wins. Report success to the caller, but leave the store for the mutation to settle.
                return memory;
            }
            dispatch(setUserMemory(memory));
            resyncNeeded = false;
            return memory;
        } catch (error) {
            console.error('Error fetching user memory:', error);
            return undefined;
        }
    }, [memoryService, dispatch]);

    const writeIfLatest = useCallback(
        (seq: number, memory: UserMemory) => {
            if (seq === mutationSeq) {
                dispatch(setUserMemory(memory));
            }
        },
        [dispatch],
    );

    const settleMutation = useCallback(() => {
        activeMutations -= 1;
        if (activeMutations === 0 && resyncNeeded) {
            void fetchMemory();
        }
    }, [fetchMemory]);

    const updateOptIn = useCallback(
        async (optedIn: boolean): Promise<void> => {
            const previous = store.getState().app.userMemory;
            // Optimistically reflect the toggle. Opting out also clears the list — the server purges
            // the scope — so mirror that here so the UI never shows notes under an off toggle.
            const optimistic: UserMemory = optedIn ? { ...previous, optedIn: true } : { optedIn: false, notes: [] };
            const seq = armMutation();
            try {
                dispatch(setUserMemory(optimistic));
                const confirmed = await memoryService.updateOptInAsync(optedIn);
                writeIfLatest(seq, confirmed);
            } catch (error) {
                console.error('Error updating memory opt-in, reverting:', error);
                writeIfLatest(seq, previous);
                throw error;
            } finally {
                settleMutation();
            }
        },
        [memoryService, dispatch, store, writeIfLatest, settleMutation],
    );

    const deleteNote = useCallback(
        async (id: string): Promise<void> => {
            const previous = store.getState().app.userMemory;
            const optimistic: UserMemory = { ...previous, notes: previous.notes.filter((n) => n.id !== id) };
            const seq = armMutation();
            try {
                dispatch(setUserMemory(optimistic));
                await memoryService.deleteNoteAsync(id);
            } catch (error) {
                console.error('Error deleting memory note, reverting:', error);
                writeIfLatest(seq, previous);
                throw error;
            } finally {
                settleMutation();
            }
        },
        [memoryService, dispatch, store, writeIfLatest, settleMutation],
    );

    const deleteAll = useCallback(async (): Promise<void> => {
        const previous = store.getState().app.userMemory;
        const optimistic: UserMemory = { ...previous, notes: [] };
        const seq = armMutation();
        try {
            dispatch(setUserMemory(optimistic));
            await memoryService.deleteAllAsync();
        } catch (error) {
            console.error('Error deleting all memory, reverting:', error);
            writeIfLatest(seq, previous);
            throw error;
        } finally {
            settleMutation();
        }
    }, [memoryService, dispatch, store, writeIfLatest, settleMutation]);

    return {
        fetchMemory,
        updateOptIn,
        deleteNote,
        deleteAll,
    };
};
